// Storm geometry — pure, dependency-free, RN-portable. Ring extraction, point-in-
// polygon, centroid, and the ETA projection that turns an NWS storm-motion vector
// into "arrives in ~N min" for the user's position relative to the cell.
//
// All coordinates are [lon, lat] (GeoJSON / NWS order) unless a name says otherwise.

import type { LngLat, NwsGeometry } from '@/lib/nws/types'
import type { StormMotion } from './motion'

const MI_PER_M = 1 / 1609.344

/** Every outer ring of a Polygon/MultiPolygon, flattened. (Holes are ignored — warning
 *  polygons are simple, and a hole would only ever shrink the hit area.) */
export function outerRings(geometry: NwsGeometry | null): LngLat[][] {
  if (!geometry) return []
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates as LngLat[][]
    return rings.length ? [rings[0]] : []
  }
  const polys = geometry.coordinates as LngLat[][][]
  return polys.map((p) => p[0]).filter(Boolean)
}

/** Ray-casting point-in-ring test (lon/lat plane). */
function inRing(lon: number, lat: number, ring: LngLat[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** Is (lat, lon) inside the warning polygon? False when there is no polygon. */
export function pointInGeometry(lat: number, lon: number, geometry: NwsGeometry | null): boolean {
  return outerRings(geometry).some((ring) => inRing(lon, lat, ring))
}

export interface LatLon {
  lat: number
  lon: number
}

/** Area-less average of a ring's vertices — good enough to center the map / anchor motion. */
export function centroid(points: LatLon[]): LatLon | null {
  if (!points.length) return null
  let lat = 0
  let lon = 0
  for (const p of points) {
    lat += p.lat
    lon += p.lon
  }
  return { lat: lat / points.length, lon: lon / points.length }
}

/** Centroid of the warning polygon (first outer ring), in {lat, lon}. */
export function geometryCentroid(geometry: NwsGeometry | null): LatLon | null {
  const ring = outerRings(geometry)[0]
  if (!ring) return null
  return centroid(ring.map(([lon, lat]) => ({ lat, lon })))
}

/** Local east/north offset (meters) of `p` from `origin` — equirectangular, fine at storm scale. */
function toLocalMeters(origin: LatLon, p: LatLon): { east: number; north: number } {
  const R = 6_371_000
  const latRad = (origin.lat * Math.PI) / 180
  const east = ((p.lon - origin.lon) * Math.PI) / 180 * R * Math.cos(latRad)
  const north = ((p.lat - origin.lat) * Math.PI) / 180 * R
  return { east, north }
}

export type EtaStatus = 'now' | 'approaching' | 'departing' | 'stationary' | 'unknown'

export interface Eta {
  status: EtaStatus
  /** Whole minutes until the cell reaches the user (only when status==='approaching'). */
  minutes: number | null
  /** Straight-line distance from the storm reference point to the user, miles. */
  distanceMi: number | null
}

/**
 * ETA from the storm-motion vector. We take the storm's current reference point
 * (motion LOC centroid, else the polygon centroid), build the unit vector it's
 * travelling *toward*, and measure how far the user lies *along* that track. If the
 * user is already inside the polygon → "now". Behind the storm → "departing".
 */
export function estimateEta(
  user: LatLon,
  motion: StormMotion | null,
  geometry: NwsGeometry | null,
  userInside: boolean,
): Eta {
  if (userInside) return { status: 'now', minutes: 0, distanceMi: 0 }

  const ref = (motion && centroid(motion.points)) || geometryCentroid(geometry)
  if (!ref) return { status: 'unknown', minutes: null, distanceMi: null }

  const { east, north } = toLocalMeters(ref, user)
  const distM = Math.hypot(east, north)
  const distanceMi = Math.round(distM * MI_PER_M)

  if (!motion || motion.speedKt <= 0) {
    return { status: 'stationary', minutes: null, distanceMi }
  }

  // Heading the storm travels toward, as an east/north unit vector (bearing from north, CW).
  const rad = (motion.towardDeg * Math.PI) / 180
  const ux = Math.sin(rad)
  const uy = Math.cos(rad)
  const alongM = east * ux + north * uy // signed: + means user is downstream

  if (alongM <= 0) return { status: 'departing', minutes: null, distanceMi }

  const speedMps = motion.speedKt * 0.514444
  const minutes = Math.max(0, Math.round(alongM / speedMps / 60))
  return { status: 'approaching', minutes, distanceMi }
}
